import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MomentOfTruth, AIAttribution } from '../components/MomentOfTruth';
import {
  Upload, FileAudio, Image, User, Loader2, CheckCircle2, AlertCircle,
  Trash2, Play, ChevronDown, ChevronRight, ArrowLeft, RefreshCw, Download,
  Clock, Save, X, FileText, Music, Headphones, Camera, BarChart3,
  TrendingUp, TrendingDown, Minus, Shield, Target, Zap, Brain
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CALL_TYPES = [
  { value: 'high_pressure_sales', label: 'High-Pressure Sales', desc: 'Urgency, objection handling, close attempts' },
  { value: 'consultative', label: 'Consultative', desc: 'Needs discovery, personalization, trust-building' },
  { value: 'order_taker', label: 'Order Taker', desc: 'Efficiency, accuracy, process adherence' },
  { value: 'information_only', label: 'Information-Only', desc: 'Clarity, completeness, professionalism' },
];
const PROSPECT_TYPES = ['New Inquiry', 'Returning Lead', 'Walk-in Follow-up', 'Web Lead', 'Referral'];
const STAFF_ROLES = [
  { value: 'qualifiers', label: 'Qualifiers' },
  { value: 'admissions_advisor', label: 'Admissions Advisor' },
  { value: 'financial_aid_officer', label: 'Financial Aid Officer' },
  { value: 'student_services', label: 'Student Services' },
];
const CALL_CATEGORIES = [
  { value: 'introductory', label: 'Introductory Call' },
  { value: 'follow_up', label: 'Follow-Up Call' },
  { value: 'objection_handling', label: 'Objection Handling' },
  { value: 'closing', label: 'Closing Call' },
  { value: 're_engagement', label: 'Re-Engagement' },
  { value: 'document_review', label: 'Document Review' },
  { value: 'financial_discussion', label: 'Financial Discussion' },
  { value: 'other', label: 'Other' },
];
const CAMPUSES = [
  'Toronto', 'North York', 'Mississauga', 'Brampton', 'Hamilton',
  'Brantford', 'Kitchener', 'London', 'Windsor', 'Online',
];

const statusColors = {
  unmapped: 'bg-rose-50 text-rose-700 border-rose-200',
  mapped: 'bg-sky-50 text-sky-700 border-sky-200',
  queued: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  transcribing: 'bg-amber-50 text-amber-700 border-amber-200',
  transcribed: 'bg-teal-50 text-teal-700 border-teal-200',
  analyzing: 'bg-amber-50 text-amber-700 border-amber-200',
  analyzed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  transcription_failed: 'bg-rose-50 text-rose-700 border-rose-200',
  analysis_failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

const gradeColors = {
  A: 'text-[#1E426C] bg-[#EEF1F6] border-[#1E426C]',
  B: 'text-[#1E426C] bg-[#EEF1F6] border-[#DDE3EC]',
  C: 'text-amber-700 bg-amber-50 border-amber-200',
  D: 'text-[#A21D23] bg-rose-50 border-rose-200',
  F: 'text-white bg-[#A21D23] border-[#A21D23]',
};

const MetadataTag = ({ label, value }) => {
  if (!value) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', backgroundColor: '#EEF1F6', color: '#5B6572', padding: '1px 8px', borderRadius: '999px', border: '1px solid #DDE3EC', fontFamily: 'Source Sans 3, sans-serif' }}>
      {label && <span style={{ fontWeight: 700, color: '#8B95A1' }}>{label}</span>} {value}
    </span>
  );
};

const ScoreBar = ({ label, value, max = 10 }) => {
  const pct = Math.min((value / max) * 100, 100);
  const color = value >= 8 ? '#1E426C' : value >= 6 ? '#2980B9' : value >= 4 ? '#D8434B' : '#A21D23';
  return (
    <div className="flex items-center gap-2">
      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 600, color: '#5B6572', width: '112px', flexShrink: 0, textAlign: 'right' }}>{label}</span>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, color: '#1D2F47', width: '28px' }}>{value}</span>
    </div>
  );
};

/* ─── Inline Mapping Form ─── */
const MappingForm = ({ upload, advisors, onSave, onCancel }) => {
  const [advisor, setAdvisor] = useState(upload.advisor_name || '');
  const [newAdvisor, setNewAdvisor] = useState('');
  const [callType, setCallType] = useState(upload.call_type || '');
  const [prospectType, setProspectType] = useState(upload.prospect_type || '');
  const [staffRole, setStaffRole] = useState(upload.staff_role || '');
  const [leadId, setLeadId] = useState(upload.lead_id || '');
  const [campus, setCampus] = useState(upload.campus || '');
  const [callCategory, setCallCategory] = useState(upload.call_category || '');
  const [notes, setNotes] = useState(upload.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const advisorName = newAdvisor.trim() || advisor;
    if (!advisorName || !callType) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/self-audit/upload/${upload.id}/map`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ advisor_name: advisorName, call_type: callType, prospect_type: prospectType, staff_role: staffRole, lead_id: leadId, campus, call_category: callCategory, notes }),
      });
      if (res.ok) onSave();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const labelStyle = { fontSize: '10px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' };
  const inputStyle = { border: '1px solid #DDE3EC', borderRadius: '6px' };

  return (
    <div className="mt-3 rounded-lg p-4 space-y-3" style={{ backgroundColor: '#FAFBFC', border: '1px solid #DDE3EC' }} data-testid={`map-form-${upload.id}`}>
      <div className="flex items-center justify-between">
        <p style={{ fontSize: '10px', fontWeight: 700, color: '#1E426C', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Map This File</p>
        {onCancel && <button onClick={onCancel} style={{ color: '#8B95A1' }} className="hover:opacity-70"><X size={14} /></button>}
      </div>

      {/* Row 1: Advisor + Campus */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label style={labelStyle}>Staff Member *</label>
          <div className="flex gap-1.5 mt-1">
            {advisors.length > 0 && (
              <select value={advisor} onChange={e => { setAdvisor(e.target.value); if (e.target.value) setNewAdvisor(''); }}
                className="flex-1 px-2 py-2 rounded text-xs bg-white outline-none" style={inputStyle} data-testid={`advisor-select-${upload.id}`}>
                <option value="">Select...</option>
                {advisors.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            <input value={newAdvisor} onChange={e => { setNewAdvisor(e.target.value); if (e.target.value) setAdvisor(''); }}
              className="flex-1 px-2 py-2 rounded text-xs outline-none" style={inputStyle} placeholder="Or new name..."
              data-testid={`advisor-input-${upload.id}`} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Campus</label>
          <select value={campus} onChange={e => setCampus(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-xs bg-white outline-none" style={inputStyle} data-testid={`campus-${upload.id}`}>
            <option value="">Select campus...</option>
            {CAMPUSES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Row 2: Staff Role */}
      <div>
        <label style={labelStyle}>Staff Role</label>
        <div className="grid grid-cols-4 gap-1.5 mt-1">
          {STAFF_ROLES.map(sr => (
            <button key={sr.value} onClick={() => setStaffRole(sr.value)} data-testid={`staff-role-${sr.value}-${upload.id}`}
              className="text-left p-2 transition-all" style={{
                border: staffRole === sr.value ? '1px solid #A21D23' : '1px solid #DDE3EC',
                backgroundColor: staffRole === sr.value ? '#FEF2F2' : 'white', borderRadius: '6px',
              }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: staffRole === sr.value ? '#A21D23' : '#2F2F2F' }}>{sr.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Row 3: Analysis Type */}
      <div>
        <label style={labelStyle}>Analysis Type *</label>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          {CALL_TYPES.map(ct => (
            <button key={ct.value} onClick={() => setCallType(ct.value)} data-testid={`call-type-${ct.value}-${upload.id}`}
              className="text-left p-2.5 transition-all" style={{
                border: callType === ct.value ? '1px solid #1E426C' : '1px solid #DDE3EC',
                backgroundColor: callType === ct.value ? '#EEF1F6' : 'white', borderRadius: '6px',
              }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: callType === ct.value ? '#1E426C' : '#2F2F2F' }}>{ct.label}</p>
              <p style={{ fontSize: '9px', color: '#8B95A1' }}>{ct.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Row 4: Call Category */}
      <div>
        <label style={labelStyle}>Type of Call</label>
        <div className="grid grid-cols-4 gap-1.5 mt-1">
          {CALL_CATEGORIES.map(cc => (
            <button key={cc.value} onClick={() => setCallCategory(cc.value)} data-testid={`call-cat-${cc.value}-${upload.id}`}
              className="text-center p-2 transition-all" style={{
                border: callCategory === cc.value ? '1px solid #1E426C' : '1px solid #DDE3EC',
                backgroundColor: callCategory === cc.value ? '#EEF1F6' : 'white', borderRadius: '6px',
              }}>
              <p style={{ fontSize: '10px', fontWeight: 600, color: callCategory === cc.value ? '#1E426C' : '#2F2F2F' }}>{cc.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Row 5: Prospect Type + Lead ID */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label style={labelStyle}>Prospect Type</label>
          <select value={prospectType} onChange={e => setProspectType(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-xs bg-white outline-none" style={inputStyle} data-testid={`prospect-${upload.id}`}>
            <option value="">Optional...</option>
            {PROSPECT_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Lead ID / Reference #</label>
          <input value={leadId} onChange={e => setLeadId(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-xs outline-none" style={inputStyle} placeholder="Student ref or lead ID..."
            data-testid={`lead-id-${upload.id}`} />
        </div>
      </div>

      {/* Row 6: Notes */}
      <div>
        <label style={labelStyle}>Notes</label>
        <input value={notes} onChange={e => setNotes(e.target.value)}
          className="w-full mt-1 px-3 py-2 text-xs outline-none" style={inputStyle} placeholder="Context..."
          data-testid={`notes-${upload.id}`} />
      </div>

      <button onClick={handleSave} disabled={saving || (!advisor && !newAdvisor) || !callType}
        className="w-full py-2 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-opacity disabled:opacity-40"
        style={{ backgroundColor: '#1E426C', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase' }}
        data-testid={`save-map-${upload.id}`}>
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        {saving ? 'Saving...' : 'Save Mapping'}
      </button>
    </div>
  );
};

/* ─── Individual Analysis Card ─── */
const AnalysisCard = ({ analysis }) => {
  const [expanded, setExpanded] = useState(false);
  if (!analysis) return null;
  const scores = analysis.scores || analysis.performance_scores || {};
  const roleQLabels = analysis.role_question_labels || {};
  const roleDimLabels = analysis.role_dimension_labels || {};
  const staffRole = analysis.staff_role_evaluated || 'admissions_advisor';

  // Build dimension score bars dynamically from the analysis
  const dimensionBars = Object.entries(roleDimLabels).map(([key, label]) => ({
    label,
    value: scores[key],
  })).filter(d => d.value != null);

  // Fallback for old analyses without role metadata
  if (dimensionBars.length === 0) {
    [['Tone & Empathy', 'tone_empathy'], ['Info Accuracy', 'information_accuracy'], ['Compliance', 'compliance'],
     ['Consultative', 'consultative_skill'], ['Closing', 'closing_ability'], ['Persistence', 'persistence']]
    .forEach(([label, key]) => { if (scores[key] != null) dimensionBars.push({ label, value: scores[key] }); });
  }

  return (
    <div className="mt-3 rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #1E426C' }} data-testid="analysis-card">
      <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[#EEF1F6]/50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 flex-wrap">
          <BarChart3 size={14} style={{ color: '#1E426C' }} />
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#1E426C', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Analysis Results</span>
          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${gradeColors[analysis.grade] || 'text-slate-600 bg-slate-50 border-slate-200'}`}>
            {analysis.grade}
          </span>
          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1D2F47' }}>{scores.overall ? `${scores.overall}/10` : ''}</span>
          {analysis.detected_call_type_label && (
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '2px 8px', borderRadius: '999px', border: '1px solid #DDE3EC' }}>
              {analysis.detected_call_type_label}
            </span>
          )}
          {staffRole !== 'admissions_advisor' && (
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '2px 8px', borderRadius: '999px', border: '1px solid #FECACA' }} data-testid="role-eval-badge">
              {staffRole === 'financial_aid_officer' ? 'FAO Rubric' : staffRole === 'qualifiers' ? 'Qualifier Rubric' : staffRole === 'student_services' ? 'Student Svc Rubric' : staffRole}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown size={14} style={{ color: '#8B95A1' }} /> : <ChevronRight size={14} style={{ color: '#8B95A1' }} />}
      </div>

      {/* Summary — always visible */}
      <div className="px-4 pb-3">
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#2F2F2F', lineHeight: 1.6 }}>{analysis.summary || analysis.score_reasoning?.quality || 'Summary unavailable — try re-analyzing this file.'}</p>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4" style={{ borderTop: '1px solid #DDE3EC' }}>
          {/* Dimension Scores (role-aware) */}
          <div className="pt-3 space-y-1.5">
            <ScoreBar label="Overall" value={scores.overall} />
            {dimensionBars.map(d => (
              <ScoreBar key={d.label} label={d.label} value={d.value} />
            ))}
          </div>

          {/* Strengths & Weaknesses */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg p-3" style={{ backgroundColor: '#EEF1F6' }}>
              <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', fontWeight: 700, color: '#1E426C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Strengths</p>
              {(analysis.strengths || []).map((s, i) => (
                <p key={i} className="flex items-start gap-1.5 mb-1" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#2F2F2F' }}>
                  <CheckCircle2 size={11} className="mt-0.5 shrink-0" style={{ color: '#1E426C' }} /> {s}
                </p>
              ))}
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: '#FEF2F2' }}>
              <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', fontWeight: 700, color: '#A21D23', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Areas for Improvement</p>
              {(analysis.weaknesses || []).map((w, i) => (
                <p key={i} className="flex items-start gap-1.5 mb-1" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#2F2F2F' }}>
                  <AlertCircle size={11} className="mt-0.5 shrink-0" style={{ color: '#A21D23' }} /> {w}
                </p>
              ))}
            </div>
          </div>

          {/* Key Moments */}
          {(analysis.key_moments || []).length > 0 && (
            <div>
              <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Key Moments</p>
              {analysis.key_moments.map((m, i) => (
                <div key={i} className="rounded-lg p-2.5 mb-1.5" style={{ backgroundColor: '#FAFBFC', border: '1px solid #EEF1F6' }}>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', fontWeight: 700, color: '#1E426C' }}>{m.timestamp_or_position || m.position}</p>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#2F2F2F', fontStyle: 'italic' }}>"{m.quote}"</p>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#5B6572' }}>{m.assessment}</p>
                </div>
              ))}
            </div>
          )}

          {/* Moment of Truth — Color-coded highlights */}
          {(analysis.moment_of_truth || []).length > 0 && (
            <MomentOfTruth moments={analysis.moment_of_truth} title="Moment of Truth" />
          )}

          {/* AI Attribution — Score Reasoning */}
          {analysis.score_reasoning && (
            <AIAttribution
              reasoning={analysis.score_reasoning}
              auditScores={analysis.audit_scores}
              roleQuestionLabels={analysis.role_question_labels}
            />
          )}

          {/* Recommendations */}
          {(analysis.recommendations || []).length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">Recommendations</p>
              {analysis.recommendations.map((r, i) => (
                <p key={i} className="text-[11px] text-slate-600 flex items-start gap-1 mb-0.5">
                  <Target size={10} className="text-indigo-400 mt-0.5 shrink-0" /> {r}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Batch Report Card ─── */
const BatchReportCard = ({ report, onClose, onDelete }) => {
  const a = report.analysis || {};
  const scores = a.cumulative_scores || {};
  const trendIcon = a.performance_trend === 'improving' ? <TrendingUp size={14} className="text-emerald-500" />
    : a.performance_trend === 'declining' ? <TrendingDown size={14} className="text-rose-500" />
    : <Minus size={14} className="text-slate-400" />;

  const riskColor = a.risk_level === 'high' ? 'text-rose-600 bg-rose-50 border-rose-200'
    : a.risk_level === 'medium' ? 'text-amber-600 bg-amber-50 border-amber-200'
    : 'text-emerald-600 bg-emerald-50 border-emerald-200';

  return (
    <div className="bg-white rounded-2xl border border-indigo-200 shadow-lg p-6 mb-6" data-testid="batch-report-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Brain size={20} className="text-indigo-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-800">Cumulative Batch Report</h3>
            <p className="text-xs text-slate-400">{report.advisor_name} — {report.upload_count} interactions analyzed</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${gradeColors[a.cumulative_grade] || ''}`}>
            Grade: {a.cumulative_grade}
          </span>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${riskColor}`}>
            <Shield size={10} className="inline mr-1" />{a.risk_level} risk
          </span>
          <span className="flex items-center gap-1 text-xs font-medium text-slate-500">
            {trendIcon} {a.performance_trend}
          </span>
          {onClose && <button onClick={onClose} className="text-slate-300 hover:text-slate-500 ml-2"><X size={16} /></button>}
          {onDelete && (
            <button onClick={onDelete} className="text-slate-300 hover:text-rose-500 ml-1" data-testid={`delete-batch-report-${report.id}`}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Executive Summary */}
      <p className="text-sm text-slate-700 mb-4 leading-relaxed">{a.executive_summary}</p>

      {/* Scores */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 mb-4">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Cumulative Scores</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
          <ScoreBar label="Overall" value={scores.overall} />
          <ScoreBar label="Tone & Empathy" value={scores.tone_empathy} />
          <ScoreBar label="Persistence" value={scores.persistence} />
          <ScoreBar label="Info Accuracy" value={scores.information_accuracy} />
          <ScoreBar label="Consultative" value={scores.consultative_skill} />
          <ScoreBar label="Compliance" value={scores.compliance} />
          <ScoreBar label="Closing" value={scores.closing_ability} />
          <ScoreBar label="Objections" value={scores.objection_handling} />
        </div>
      </div>

      {/* Call Type Distribution */}
      {a.call_type_distribution && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Call Types:</span>
          {Object.entries(a.call_type_distribution).map(([type, count]) => (
            <span key={type} className="text-[10px] bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full font-bold border border-violet-200">
              {type.replace(/_/g, ' ')}: {count}
            </span>
          ))}
        </div>
      )}

      {/* Strengths, Weaknesses, Patterns */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1.5">Top Strengths</p>
          {(a.top_strengths || []).map((s, i) => (
            <p key={i} className="text-[11px] text-slate-600 flex items-start gap-1 mb-1">
              <CheckCircle2 size={10} className="text-emerald-500 mt-0.5 shrink-0" /> {s}
            </p>
          ))}
        </div>
        <div>
          <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-1.5">Critical Weaknesses</p>
          {(a.critical_weaknesses || []).map((w, i) => (
            <p key={i} className="text-[11px] text-slate-600 flex items-start gap-1 mb-1">
              <AlertCircle size={10} className="text-rose-500 mt-0.5 shrink-0" /> {w}
            </p>
          ))}
        </div>
        <div>
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1.5">Patterns</p>
          {(a.patterns_observed || []).map((p, i) => (
            <p key={i} className="text-[11px] text-slate-600 flex items-start gap-1 mb-1">
              <Zap size={10} className="text-indigo-400 mt-0.5 shrink-0" /> {p}
            </p>
          ))}
        </div>
      </div>

      {/* Action Items */}
      {(a.action_items || []).length > 0 && (
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1.5">Action Items</p>
          {a.action_items.map((item, i) => (
            <p key={i} className="text-[11px] text-slate-700 flex items-start gap-1.5 mb-1">
              <Target size={10} className="text-indigo-500 mt-0.5 shrink-0" /> {item}
            </p>
          ))}
        </div>
      )}

      <p className="text-[9px] text-slate-300 mt-3 font-mono">Report generated {new Date(report.created_at).toLocaleString()}</p>
    </div>
  );
};

/* ─── Main Self-Audit Page ─── */
const SelfAudit = ({ embeddedMode = false, onDownloadPdf: propDownloadPdf, onDownloadBatchPdf: propDownloadBatchPdf }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Default PDF download handler for standalone mode
  const defaultDownloadPdf = async (url) => {
    try {
      const res = await fetch(`${API}${url}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = url.split('/').pop() + '.pdf';
        a.click();
        URL.revokeObjectURL(a.href);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'PDF download failed');
      }
    } catch (e) { alert('Download failed'); }
  };
  const onDownloadPdf = propDownloadPdf || ((id) => defaultDownloadPdf(`/api/self-audit/pdf/analysis/${id}`));
  const onDownloadBatchPdf = propDownloadBatchPdf || ((name) => defaultDownloadPdf(`/api/self-audit/pdf/batch/${encodeURIComponent(name)}`));
  const [uploads, setUploads] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [editingMap, setEditingMap] = useState({});
  const [expandedTranscript, setExpandedTranscript] = useState({});
  const [batchReports, setBatchReports] = useState([]);
  const [generatingBatch, setGeneratingBatch] = useState({});
  const [activeView, setActiveView] = useState('files'); // 'files' or 'reports'

  const fetchUploads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/uploads`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUploads(data.uploads || []);
        setAdvisors(data.advisors || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/batch-reports`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBatchReports(data.reports || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchUploads(); fetchReports(); }, [fetchUploads, fetchReports]);

  const handleUploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await fetch(`${API}/api/self-audit/upload`, { method: 'POST', credentials: 'include', body: formData });
      } catch (e) { console.error(e); }
    }
    setUploading(false);
    fetchUploads();
  };

  const handleFileSelect = (e) => handleUploadFiles(Array.from(e.target.files || []));
  const handleDrop = (e) => { e.preventDefault(); handleUploadFiles(Array.from(e.dataTransfer.files)); };

  const handleTranscribe = async (uploadId) => {
    setProcessing(p => ({ ...p, [uploadId]: 'transcribing' }));
    try {
      const res = await fetch(`${API}/api/self-audit/upload/${uploadId}/transcribe`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Transcription failed: ${err.detail || res.statusText}`);
        setProcessing(p => ({ ...p, [uploadId]: null }));
        return;
      }
      // Kick off analysis (returns 202 — runs in background)
      setProcessing(p => ({ ...p, [uploadId]: 'analyzing' }));
      const analyzeRes = await fetch(`${API}/api/self-audit/upload/${uploadId}/analyze`, { method: 'POST', credentials: 'include' });
      if (analyzeRes.status === 202 || analyzeRes.ok) {
        // Poll for completion
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const check = await fetch(`${API}/api/self-audit/upload/${uploadId}`, { credentials: 'include' });
            if (check.ok) {
              const data = await check.json();
              if (data.status === 'analyzed' || data.status === 'analysis_failed') {
                clearInterval(poll);
                setProcessing(p => ({ ...p, [uploadId]: null }));
                fetchUploads();
              }
            }
          } catch (_) { /* continue polling */ }
          if (attempts >= 30) { // 2.5 min max
            clearInterval(poll);
            setProcessing(p => ({ ...p, [uploadId]: null }));
            fetchUploads();
          }
        }, 5000);
      } else {
        const err = await analyzeRes.json().catch(() => ({}));
        const msg = err.detail || 'Analysis failed';
        if (analyzeRes.status === 402) {
          alert(`Token cap reached. ${msg}\n\nContact your admin to increase your token limit.`);
        } else {
          alert(`Analysis failed: ${msg}`);
        }
        fetchUploads();
        setProcessing(p => ({ ...p, [uploadId]: null }));
      }
    } catch (e) {
      console.error(e);
      setProcessing(p => ({ ...p, [uploadId]: null }));
    }
  };

  const handleProcessAll = async () => {
    const targets = uploads.filter(u => ['mapped', 'transcribed', 'transcription_failed', 'analysis_failed'].includes(u.status) && u.advisor_name && u.call_type);
    if (!targets.length) return;
    setProcessing(p => ({ ...p, _all: true }));
    try {
      const res = await fetch(`${API}/api/self-audit/bulk-process`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      if (res.status === 402) {
        const err = await res.json().catch(() => ({}));
        alert(`Token cap reached. ${err.detail || ''}\n\nContact your admin to increase your token limit.`);
        setProcessing({});
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Bulk processing failed: ${err.detail || res.statusText}`);
        setProcessing({});
        return;
      }
    } catch (e) { console.error(e); setProcessing({}); return; }

    // Poll until nothing is in-flight anymore (queued/transcribing/analyzing)
    await fetchUploads();
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      let list = [];
      try {
        const r = await fetch(`${API}/api/self-audit/uploads`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); list = d.uploads || []; setUploads(list); }
      } catch (_) { /* keep polling */ }
      const inFlight = list.some(u => ['queued', 'transcribing', 'analyzing'].includes(u.status));
      if (!inFlight || attempts >= 60) { // up to 5 min
        clearInterval(poll);
        setProcessing({});
        fetchUploads();
      }
    }, 5000);
  };

  const handleBatchReport = async (advisorName) => {
    setGeneratingBatch(p => ({ ...p, [advisorName]: true }));
    try {
      const res = await fetch(`${API}/api/self-audit/batch-report`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ advisor_name: advisorName }),
      });
      if (res.ok) { fetchReports(); }
    } catch (e) { console.error(e); }
    finally { setGeneratingBatch(p => ({ ...p, [advisorName]: false })); }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Delete this upload permanently?')) return;
    try {
      await fetch(`${API}/api/self-audit/upload/${uploadId}`, { method: 'DELETE', credentials: 'include' });
      fetchUploads();
    } catch (e) { console.error(e); }
  };

  const handleRetryAnalysis = async (uploadId) => {
    setProcessing(p => ({ ...p, [uploadId]: 'analyzing' }));
    try {
      const analyzeRes = await fetch(`${API}/api/self-audit/upload/${uploadId}/analyze`, { method: 'POST', credentials: 'include' });
      if (analyzeRes.status === 202 || analyzeRes.ok) {
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const check = await fetch(`${API}/api/self-audit/upload/${uploadId}`, { credentials: 'include' });
            if (check.ok) {
              const data = await check.json();
              if (data.status === 'analyzed' || data.status === 'analysis_failed') {
                clearInterval(poll);
                setProcessing(p => ({ ...p, [uploadId]: null }));
                fetchUploads();
              }
            }
          } catch (_) { /* continue polling */ }
          if (attempts >= 30) {
            clearInterval(poll);
            setProcessing(p => ({ ...p, [uploadId]: null }));
            fetchUploads();
          }
        }, 5000);
      } else {
        const err = await analyzeRes.json().catch(() => ({}));
        const msg = err.detail || 'Analysis failed';
        if (analyzeRes.status === 402) {
          alert(`Token cap reached. ${msg}\n\nContact your admin to increase your token limit.`);
        } else {
          alert(`Retry failed: ${msg}`);
        }
        setProcessing(p => ({ ...p, [uploadId]: null }));
        fetchUploads();
      }
    } catch (e) {
      console.error(e);
      setProcessing(p => ({ ...p, [uploadId]: null }));
    }
  };

  const handleResetAnalysis = async (uploadId) => {
    if (!window.confirm('Reset this analysis? The file will go back to "transcribed" state and can be re-analyzed.')) return;
    try {
      const res = await fetch(`${API}/api/self-audit/upload/${uploadId}/reset-analysis`, { method: 'POST', credentials: 'include' });
      if (res.ok) fetchUploads();
      else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Reset failed'); }
    } catch (e) { console.error(e); }
  };

  const handleDeleteBatchReport = async (reportId) => {
    if (!window.confirm('Delete this batch report?')) return;
    try {
      const res = await fetch(`${API}/api/self-audit/batch-report/${reportId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchReports();
      else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Delete failed'); }
    } catch (e) { console.error(e); }
  };

  // Feedback
  const [feedbackModal, setFeedbackModal] = useState(null); // upload ID
  const [feedbackForm, setFeedbackForm] = useState({ rating: 3, accuracy_issues: '', scoring_disagreements: '', general_notes: '' });

  const handleSubmitFeedback = async () => {
    if (!feedbackModal) return;
    try {
      const res = await fetch(`${API}/api/self-audit/upload/${feedbackModal}/feedback`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackForm),
      });
      if (res.ok) {
        setFeedbackModal(null);
        setFeedbackForm({ rating: 3, accuracy_issues: '', scoring_disagreements: '', general_notes: '' });
        fetchUploads();
      } else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Feedback failed'); }
    } catch (e) { console.error(e); }
  };

  // Group uploads by role, then by advisor within each role
  const roleGroups = {};
  const ROLE_ORDER = ['admissions_advisor', 'financial_aid_officer', 'qualifiers', 'student_services', ''];
  const ROLE_LABELS = { admissions_advisor: 'Admissions Advisors', financial_aid_officer: 'Financial Aid Officers', qualifiers: 'Qualifiers', student_services: 'Student Services', '': 'Unassigned Role' };
  const ROLE_COLORS = { admissions_advisor: '#1E426C', financial_aid_officer: '#A21D23', qualifiers: '#2980B9', student_services: '#16a34a', '': '#8B95A1' };

  uploads.forEach(u => {
    if (!u.advisor_name) return; // unmapped handled separately
    const role = u.staff_role || '';
    if (!roleGroups[role]) roleGroups[role] = {};
    const advisor = u.advisor_name;
    if (!roleGroups[role][advisor]) roleGroups[role][advisor] = [];
    roleGroups[role][advisor].push(u);
  });

  const [expandedAdvisor, setExpandedAdvisor] = useState({});
  const [expandedRole, setExpandedRoleSection] = useState({});

  const toggleAdvisor = (key) => setExpandedAdvisor(p => ({ ...p, [key]: !p[key] }));
  const toggleRole = (key) => setExpandedRoleSection(p => ({ ...p, [key]: !p[key] }));

  const unmappedUploads = uploads.filter(u => !u.advisor_name);

  const totalCount = uploads.length;
  const unmappedCount = unmappedUploads.length;
  const readyCount = uploads.filter(u => ['mapped', 'transcribed', 'transcription_failed', 'analysis_failed'].includes(u.status) && u.advisor_name && u.call_type).length;
  const analyzedCount = uploads.filter(u => u.status === 'analyzed').length;

  const statusLabel = (s) => {
    const map = { unmapped: 'NEEDS MAPPING', mapped: 'READY', transcribing: 'TRANSCRIBING', transcribed: 'TRANSCRIBED',
      analyzing: 'ANALYZING', analyzed: 'ANALYZED', transcription_failed: 'FAILED', analysis_failed: 'ANALYSIS FAILED' };
    return map[s] || s;
  };

  return (
    <div className={embeddedMode ? "" : "min-h-screen"} style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
      {!embeddedMode && (
      <header className="bg-white sticky top-0 z-50" style={{ borderBottom: '1px solid #DDE3EC' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard/client')} style={{ color: '#8B95A1' }} className="hover:opacity-70 transition-opacity" data-testid="back-to-dashboard">
              <ArrowLeft size={18} />
            </button>
            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>Self-Audit</span>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '2px 10px', borderRadius: '999px', border: '1px solid #FECACA', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Upload & Analyze</span>
          </div>
          <div className="flex items-center gap-3">
            {readyCount > 0 && (
              <button onClick={handleProcessAll} disabled={processing._all}
                style={{ fontSize: '12px', fontWeight: 700, color: 'white', backgroundColor: '#1E426C', padding: '8px 16px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '5px', letterSpacing: '0.06em', textTransform: 'uppercase', opacity: processing._all ? 0.5 : 1 }}
                data-testid="process-all-btn">
                {processing._all ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                {processing._all ? 'Processing...' : `Process All (${readyCount})`}
              </button>
            )}
            <button onClick={() => { fetchUploads(); fetchReports(); }} style={{ color: '#8B95A1' }} className="hover:opacity-70 transition-opacity" data-testid="refresh-uploads">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </header>
      )}

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6" data-testid="self-audit-stats">
          {[
            { value: totalCount, label: 'Total Uploads', color: '#1D2F47' },
            { value: unmappedCount, label: 'Needs Mapping', color: '#A21D23' },
            { value: readyCount, label: 'Ready to Process', color: '#2980B9' },
            { value: analyzedCount, label: 'Analyzed', color: '#1E426C' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-4" style={{ border: '1px solid #DDE3EC', borderTop: `3px solid ${s.color}` }}>
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '28px', fontWeight: 700, color: s.color }}>{s.value}</p>
              <p style={{ fontSize: '10px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Sub-tabs */}
        <div className="flex items-center gap-1.5 mb-6" style={{ backgroundColor: '#EEF1F6', borderRadius: '6px', padding: '3px', width: 'fit-content' }}>
          <button onClick={() => setActiveView('files')} data-testid="tab-files"
            style={{ padding: '6px 16px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, backgroundColor: activeView === 'files' ? 'white' : 'transparent', color: activeView === 'files' ? '#1D2F47' : '#8B95A1', boxShadow: activeView === 'files' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            Files ({totalCount})
          </button>
          <button onClick={() => setActiveView('reports')} data-testid="tab-reports"
            style={{ padding: '6px 16px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, backgroundColor: activeView === 'reports' ? 'white' : 'transparent', color: activeView === 'reports' ? '#1D2F47' : '#8B95A1', boxShadow: activeView === 'reports' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            Batch Reports ({batchReports.length})
          </button>
        </div>

        {activeView === 'files' && (
          <>
            {/* Upload Area */}
            <div className="mb-6">
              <div className="bg-white rounded-lg transition-colors p-8 text-center cursor-pointer hover:border-[#1E426C]"
                style={{ border: uploading ? '2px dashed #1E426C' : '2px dashed #DDE3EC', backgroundColor: uploading ? '#EEF1F6' : 'white' }}
                onClick={() => !uploading && fileInputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}
                data-testid="upload-dropzone">
                {uploading ? (
                  <>
                    <Loader2 size={28} className="mx-auto mb-3 animate-spin" style={{ color: '#1E426C' }} />
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#1E426C' }}>Uploading files...</p>
                  </>
                ) : (
                  <>
                    <Upload size={28} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#2F2F2F' }}>Drop files here or click to browse</p>
                    <p style={{ fontSize: '12px', color: '#8B95A1', marginTop: '4px' }}>MP3, WAV, M4A (call recordings) or PNG, JPG (SMS screenshots) — Max 25MB</p>
                    <p style={{ fontSize: '11px', color: '#1E426C', fontWeight: 600, marginTop: '8px' }}>Files upload instantly — you map them to advisors after</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" multiple accept=".mp3,.wav,.m4a,.mpeg,.mpga,.webm,.mp4,.png,.jpg,.jpeg,.webp"
                  className="hidden" onChange={handleFileSelect} data-testid="file-input" />
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>
            ) : uploads.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center" style={{ border: '1px solid #DDE3EC' }}>
                <FileAudio size={28} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#2F2F2F' }}>No uploads yet</p>
                <p style={{ fontSize: '12px', color: '#8B95A1', marginTop: '4px' }}>Upload call recordings or SMS screenshots to start your self-audit</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Unmapped Files */}
                {unmappedUploads.length > 0 && (
                  <div className="bg-white rounded-lg overflow-hidden" data-testid="needs-mapping-section" style={{ border: '1px solid #FECACA', borderLeft: '3px solid #A21D23' }}>
                    <div className="px-5 py-3 flex items-center gap-2" style={{ backgroundColor: '#FEF2F2' }}>
                      <AlertCircle size={14} style={{ color: '#A21D23' }} />
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#A21D23', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Needs Mapping ({unmappedUploads.length})</span>
                    </div>
                    <div className="divide-y" style={{ borderColor: '#EEF1F6' }}>
                      {unmappedUploads.map(u => (
                        <FileCard key={u.id} u={u} advisors={advisors} editingMap={editingMap} setEditingMap={setEditingMap}
                          expandedTranscript={expandedTranscript} setExpandedTranscript={setExpandedTranscript}
                          processing={processing} onTranscribe={handleTranscribe} onDelete={handleDelete}
                          onMapSave={() => { setEditingMap(p => ({ ...p, [u.id]: false })); fetchUploads(); }}
                          onDownloadPdf={onDownloadPdf} onResetAnalysis={handleResetAnalysis}
                          onRetryAnalysis={handleRetryAnalysis}
                          onFeedback={(id) => setFeedbackModal(id)} statusLabel={statusLabel} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Role → Person → Files */}
                {ROLE_ORDER.map(role => {
                  const advisorMap = roleGroups[role];
                  if (!advisorMap || Object.keys(advisorMap).length === 0) return null;
                  const roleColor = ROLE_COLORS[role] || '#8B95A1';
                  const roleLabel = ROLE_LABELS[role] || role;
                  const advisorNames = Object.keys(advisorMap).sort((a, b) => {
                    const latestA = advisorMap[a][0]?.created_at || '';
                    const latestB = advisorMap[b][0]?.created_at || '';
                    return latestB.localeCompare(latestA);
                  });
                  const totalInRole = advisorNames.reduce((s, a) => s + advisorMap[a].length, 0);
                  const analyzedInRole = advisorNames.reduce((s, a) => s + advisorMap[a].filter(u => u.status === 'analyzed').length, 0);
                  const isRoleOpen = expandedRole[role] === true;

                  return (
                    <div key={role} className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC', borderLeft: `3px solid ${roleColor}` }} data-testid={`role-section-${role || 'unassigned'}`}>
                      {/* Role Header */}
                      <button onClick={() => toggleRole(role)} data-testid={`role-header-${role || 'unassigned'}`} className="w-full px-5 py-3 flex items-center justify-between hover:bg-[#EEF1F6]/30 transition-colors" style={{ backgroundColor: '#FAFBFC' }}>
                        <div className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: roleColor }} />
                          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>{roleLabel}</span>
                          <span style={{ fontSize: '11px', color: '#8B95A1' }}>{advisorNames.length} staff · {totalInRole} files · {analyzedInRole} analyzed</span>
                        </div>
                        {isRoleOpen ? <ChevronDown size={14} style={{ color: '#8B95A1' }} /> : <ChevronRight size={14} style={{ color: '#8B95A1' }} />}
                      </button>

                      {/* Advisors within this role */}
                      {isRoleOpen && advisorNames.map(advisorName => {
                        const group = advisorMap[advisorName];
                        const analyzedInGroup = group.filter(u => u.status === 'analyzed').length;
                        const canBatch = analyzedInGroup >= 1;
                        const advKey = `${role}_${advisorName}`;
                        const isAdvisorOpen = expandedAdvisor[advKey] === true;
                        const grades = group.map(u => u.analysis?.grade).filter(Boolean);
                        const avgGrade = grades.length > 0 ? grades.sort((a,b) => grades.filter(g=>g===b).length - grades.filter(g=>g===a).length)[0] : '-';

                        return (
                          <div key={advisorName} style={{ borderTop: '1px solid #EEF1F6' }}>
                            {/* Advisor Row */}
                            <div className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-[#EEF1F6]/20 transition-colors" data-testid={`advisor-row-${advisorName}`} onClick={() => toggleAdvisor(advKey)}>
                              <div className="flex items-center gap-3">
                                {isAdvisorOpen ? <ChevronDown size={12} style={{ color: '#8B95A1' }} /> : <ChevronRight size={12} style={{ color: '#8B95A1' }} />}
                                <User size={14} style={{ color: roleColor }} />
                                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>{advisorName}</span>
                                <span style={{ fontSize: '11px', color: '#8B95A1' }}>{group.length} file{group.length !== 1 ? 's' : ''}</span>
                                {analyzedInGroup > 0 && (
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '1px 8px', borderRadius: '999px', border: '1px solid #DDE3EC' }}>
                                    {analyzedInGroup} analyzed · {avgGrade}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                {canBatch && onDownloadBatchPdf && (
                                  <button onClick={() => onDownloadBatchPdf(advisorName)}
                                    style={{ fontSize: '10px', fontWeight: 700, color: '#5B6572', backgroundColor: '#EEF1F6', padding: '4px 10px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '3px', border: '1px solid #DDE3EC' }}
                                    data-testid={`batch-pdf-btn-${advisorName}`}>
                                    <Download size={10} /> PDF
                                  </button>
                                )}
                                {canBatch && (
                                  <button onClick={() => handleBatchReport(advisorName)} disabled={generatingBatch[advisorName]}
                                    style={{ fontSize: '10px', fontWeight: 700, color: 'white', backgroundColor: roleColor, padding: '4px 10px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '3px', opacity: generatingBatch[advisorName] ? 0.5 : 1 }}
                                    data-testid={`batch-report-btn-${advisorName}`}>
                                    {generatingBatch[advisorName] ? <Loader2 size={10} className="animate-spin" /> : <Brain size={10} />}
                                    Batch Report
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Files for this advisor */}
                            {isAdvisorOpen && (
                              <div className="pl-4" style={{ borderTop: '1px solid #EEF1F6' }}>
                                {group.map(u => (
                                  <FileCard key={u.id} u={u} advisors={advisors} editingMap={editingMap} setEditingMap={setEditingMap}
                                    expandedTranscript={expandedTranscript} setExpandedTranscript={setExpandedTranscript}
                                    processing={processing} onTranscribe={handleTranscribe} onDelete={handleDelete}
                                    onMapSave={() => { setEditingMap(p => ({ ...p, [u.id]: false })); fetchUploads(); }}
                                    onDownloadPdf={onDownloadPdf} onResetAnalysis={handleResetAnalysis}
                                    onRetryAnalysis={handleRetryAnalysis}
                                    onFeedback={(id) => setFeedbackModal(id)} statusLabel={statusLabel} />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeView === 'reports' && (
          <div>
            {batchReports.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center" style={{ border: '1px solid #DDE3EC' }}>
                <Brain size={28} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#2F2F2F' }}>No batch reports yet</p>
                <p style={{ fontSize: '12px', color: '#8B95A1', marginTop: '4px' }}>Analyze files for an advisor, then generate a batch report</p>
              </div>
            ) : (
              batchReports.map(r => <BatchReportCard key={r.id} report={r} onDelete={() => handleDeleteBatchReport(r.id)} />)
            )}
          </div>
        )}
      </main>

      {/* Feedback Modal */}
      {feedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(29,47,71,0.5)' }} data-testid="feedback-modal-overlay">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 overflow-hidden" style={{ border: '1px solid #DDE3EC', boxShadow: '0 16px 48px -12px rgba(29,47,71,0.25)' }}>
            <div className="px-6 py-4" style={{ borderBottom: '1px solid #DDE3EC', borderTop: '3px solid #A21D23' }}>
              <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>Analysis Feedback</h3>
              <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1', marginTop: '2px' }}>Your feedback improves future analyses for your organization</p>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '8px' }}>Accuracy Rating</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setFeedbackForm(f => ({...f, rating: n}))}
                      style={{ width: '40px', height: '40px', borderRadius: '6px', fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 700, border: '1px solid', borderColor: feedbackForm.rating === n ? '#A21D23' : '#DDE3EC', backgroundColor: feedbackForm.rating === n ? '#A21D23' : 'white', color: feedbackForm.rating === n ? 'white' : '#5B6572' }}
                      data-testid={`feedback-rating-${n}`}>{n}</button>
                  ))}
                </div>
                <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', marginTop: '4px' }}>1 = Very inaccurate, 5 = Spot on</p>
              </div>
              <div>
                <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>Scoring Disagreements</label>
                <textarea value={feedbackForm.scoring_disagreements} onChange={e => setFeedbackForm(f => ({...f, scoring_disagreements: e.target.value}))}
                  style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '10px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif', width: '100%', minHeight: '60px', outline: 'none' }}
                  placeholder="e.g., 'Q3 scored 0 but the advisor did mention prerequisites around the 5-minute mark'" data-testid="feedback-scoring" />
              </div>
              <div>
                <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>Accuracy Issues</label>
                <textarea value={feedbackForm.accuracy_issues} onChange={e => setFeedbackForm(f => ({...f, accuracy_issues: e.target.value}))}
                  style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '10px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif', width: '100%', minHeight: '60px', outline: 'none' }}
                  placeholder="e.g., 'The advisor correctly quoted tuition but the AI flagged it as inaccurate'" data-testid="feedback-accuracy" />
              </div>
              <div>
                <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>General Notes</label>
                <textarea value={feedbackForm.general_notes} onChange={e => setFeedbackForm(f => ({...f, general_notes: e.target.value}))}
                  style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '10px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif', width: '100%', minHeight: '60px', outline: 'none' }}
                  placeholder="Any other feedback about this analysis" data-testid="feedback-notes" />
              </div>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-3" style={{ borderTop: '1px solid #DDE3EC' }}>
              <button onClick={() => setFeedbackModal(null)} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', padding: '8px 16px' }}>Cancel</button>
              <button onClick={handleSubmitFeedback} style={{ backgroundColor: '#A21D23', color: 'white', height: '38px', padding: '0 20px', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }} data-testid="submit-feedback-btn">Submit Feedback</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── File Card Component ─── */
const FileCard = ({ u, advisors, editingMap, setEditingMap, expandedTranscript, setExpandedTranscript,
  processing, onTranscribe, onDelete, onMapSave, onDownloadPdf, onResetAnalysis, onRetryAnalysis, onFeedback, statusLabel }) => {
  const meta = u.metadata || {};
  const isAudio = u.file_type === 'audio';
  const showMap = u.status === 'unmapped' || editingMap[u.id];
  const isProcessing = processing[u.id];

  return (
    <div className="bg-white rounded-lg overflow-hidden" style={{ border: u.status === 'unmapped' ? '1px solid #FECACA' : u.status === 'analyzed' ? '1px solid #DDE3EC' : '1px solid #DDE3EC', borderLeft: u.status === 'analyzed' ? '3px solid #1E426C' : u.status === 'unmapped' ? '3px solid #A21D23' : '3px solid #DDE3EC' }}
      data-testid={`upload-row-${u.id}`}>
      <div className="px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isAudio ? '#EEF1F6' : '#FEF3C7' }}>
            {isAudio ? <Headphones size={18} style={{ color: '#1E426C' }} /> : <Camera size={18} style={{ color: '#92400E' }} />}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }} className="truncate max-w-[300px]">{u.original_filename}</span>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${statusColors[u.status] || statusColors.unmapped}`}>
                {statusLabel(u.status)}
              </span>
              {u.analysis?.detected_call_type_label && (
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '2px 8px', borderRadius: '999px', border: '1px solid #DDE3EC' }}>
                  {u.analysis.detected_call_type_label}
                </span>
              )}
              {!u.analysis?.detected_call_type_label && u.call_type_label && (
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '2px 8px', borderRadius: '999px', border: '1px solid #DDE3EC' }}>{u.call_type_label}</span>
              )}
              {u.advisor_name && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#5B6572', backgroundColor: '#FAFBFC', padding: '2px 8px', borderRadius: '999px', border: '1px solid #DDE3EC', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <User size={9} /> {u.advisor_name}
                </span>
              )}
              {u.staff_role_label && (
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '2px 8px', borderRadius: '999px', border: '1px solid #FECACA' }} data-testid={`role-tag-${u.id}`}>
                  {u.staff_role_label}
                </span>
              )}
              {u.lead_id && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#92400E', backgroundColor: '#FEF3C7', padding: '2px 8px', borderRadius: '999px', border: '1px solid #FDE68A' }} data-testid={`lead-tag-${u.id}`}>
                  Ref: {u.lead_id}
                </span>
              )}
              {u.campus && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '2px 8px', borderRadius: '999px', border: '1px solid #DDE3EC' }}>
                  {u.campus}
                </span>
              )}
              {u.call_category_label && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#2980B9', backgroundColor: '#EBF5FB', padding: '2px 8px', borderRadius: '999px', border: '1px solid #BEE3F8' }}>
                  {u.call_category_label}
                </span>
              )}
              {u.analysis?.grade && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${gradeColors[u.analysis.grade] || ''}`}>
                  {u.analysis.grade} ({(u.analysis.scores || u.analysis.performance_scores)?.overall}/10)
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              <MetadataTag value={`${(u.file_size / 1024 / 1024).toFixed(1)}MB`} />
              <MetadataTag value={meta.format?.toUpperCase()} />
              {meta.duration_display && <MetadataTag value={meta.duration_display} />}
              {meta.bitrate_display && <MetadataTag value={meta.bitrate_display} />}
              {meta.channel_label && <MetadataTag value={meta.channel_label} />}
              {meta.sample_rate && <MetadataTag value={`${meta.sample_rate}Hz`} />}
              {meta.dimensions && <MetadataTag value={meta.dimensions} />}
            </div>

            {u.notes && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1', fontStyle: 'italic', marginBottom: '4px' }}>"{u.notes}"</p>}
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', color: '#8B95A1', fontFamily: 'monospace' }}>{new Date(u.created_at).toLocaleString()}</p>

            {/* Transcript toggle */}
            {u.transcript && (
              <div className="mt-2">
                <button onClick={() => setExpandedTranscript(p => ({ ...p, [u.id]: !p[u.id] }))}
                  style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#1E426C', display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                  data-testid={`toggle-transcript-${u.id}`}>
                  {expandedTranscript[u.id] ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                  {expandedTranscript[u.id] ? 'Hide Transcript' : 'View Transcript'} ({u.transcript.length} chars)
                </button>
                {expandedTranscript[u.id] && (
                  <pre className="mt-2 rounded-lg p-4 text-xs whitespace-pre-wrap leading-relaxed max-h-[200px] overflow-y-auto" style={{ backgroundColor: '#EEF1F6', border: '1px solid #DDE3EC', color: '#2F2F2F', fontFamily: 'Source Sans 3, sans-serif' }}
                    data-testid={`transcript-content-${u.id}`}>
                    {u.transcript}
                  </pre>
                )}
              </div>
            )}

            {/* Analysis Results */}
            {u.analysis && <AnalysisCard analysis={u.analysis} />}

            {u.error && (u.status === 'analysis_failed' || u.status === 'transcription_failed') && (
              <div className="mt-2 bg-rose-50 rounded-lg px-3 py-2 text-xs text-rose-600 flex items-center gap-1.5">
                <AlertCircle size={12} /> {u.error}
              </div>
            )}

            {/* Mapping Form */}
            {showMap && (
              <MappingForm upload={u} advisors={advisors} onSave={onMapSave}
                onCancel={u.status !== 'unmapped' ? () => setEditingMap(p => ({ ...p, [u.id]: false })) : null} />
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {u.status === 'mapped' && (
              <>
                <button onClick={() => setEditingMap(p => ({ ...p, [u.id]: !p[u.id] }))}
                  style={{ fontSize: '11px', fontWeight: 600, color: '#5B6572', backgroundColor: '#EEF1F6', padding: '6px 10px', borderRadius: '3px', border: '1px solid #DDE3EC', fontFamily: 'Source Sans 3, sans-serif' }}
                  data-testid={`remap-btn-${u.id}`}>Re-map</button>
                <button onClick={() => onTranscribe(u.id)} disabled={!!isProcessing}
                  style={{ fontSize: '11px', fontWeight: 700, color: 'white', backgroundColor: '#1E426C', padding: '6px 12px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'Source Sans 3, sans-serif', opacity: isProcessing ? 0.5 : 1 }}
                  data-testid={`transcribe-btn-${u.id}`}>
                  {isProcessing ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                  {isProcessing === 'transcribing' ? 'Transcribing...' : isProcessing === 'analyzing' ? 'Analyzing...' : 'Process'}
                </button>
              </>
            )}
            {(u.status === 'transcribing' || u.status === 'analyzing') && (
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', backgroundColor: '#FEF3C7', padding: '6px 12px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'Source Sans 3, sans-serif' }}>
                <Loader2 size={11} className="animate-spin" /> {u.status === 'analyzing' ? 'Analyzing...' : 'Transcribing...'}
              </span>
            )}
            {u.status === 'transcribed' && (
              <>
                <button onClick={() => onTranscribe(u.id)} disabled={!!isProcessing} title="Runs analysis and keeps this call counted in leaderboards and reports"
                  style={{ fontSize: '11px', fontWeight: 700, color: 'white', backgroundColor: '#1E426C', padding: '6px 12px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'Source Sans 3, sans-serif', opacity: isProcessing ? 0.5 : 1 }}
                  data-testid={`analyze-btn-${u.id}`}>
                  {isProcessing ? <Loader2 size={11} className="animate-spin" /> : <BarChart3 size={11} />}
                  {isProcessing ? 'Analyzing...' : 'Include for Analysis and Archive'}
                </button>
                <button onClick={() => onDelete(u.id)} disabled={!!isProcessing} title="Permanently removes this call and its transcript"
                  style={{ fontSize: '11px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '6px 12px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #FECACA', fontFamily: 'Source Sans 3, sans-serif', opacity: isProcessing ? 0.5 : 1 }}
                  data-testid={`delete-exclude-btn-${u.id}`}>
                  <Trash2 size={11} /> Delete from Analysis and Archive
                </button>
              </>
            )}
            {u.status === 'analysis_failed' && (
              <div className="flex items-center gap-2">
                <span style={{ fontSize: '10px', color: '#A21D23', maxWidth: '200px' }} className="truncate" title={u.error}>{u.error || 'Analysis failed'}</span>
                <button onClick={() => onRetryAnalysis(u.id)} disabled={!!isProcessing}
                  style={{ fontSize: '11px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '6px 12px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #FECACA', fontFamily: 'Source Sans 3, sans-serif' }}
                  data-testid={`retry-analyze-btn-${u.id}`}>
                  {isProcessing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Retry
                </button>
              </div>
            )}
            {(u.status === 'analyzed' || u.status === 'transcribed') && (
              <>
                {u.status === 'analyzed' && onDownloadPdf && (
                  <button onClick={() => onDownloadPdf(u.id)}
                    style={{ fontSize: '11px', fontWeight: 700, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '6px 10px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #DDE3EC', fontFamily: 'Source Sans 3, sans-serif' }}
                    data-testid={`download-pdf-${u.id}`}>
                    <Download size={10} /> PDF
                  </button>
                )}
                {u.status === 'analyzed' && onResetAnalysis && (
                  <button onClick={() => onResetAnalysis(u.id)}
                    style={{ fontSize: '11px', fontWeight: 600, color: '#5B6572', backgroundColor: '#EEF1F6', padding: '6px 10px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #DDE3EC', fontFamily: 'Source Sans 3, sans-serif' }}
                    data-testid={`reset-analysis-${u.id}`}>
                    <RefreshCw size={10} /> Reset
                  </button>
                )}
                {u.status === 'analyzed' && onFeedback && (
                  <button onClick={() => onFeedback(u.id)}
                    style={{ fontSize: '11px', fontWeight: 700, color: u.feedback ? '#8B95A1' : '#A21D23', backgroundColor: u.feedback ? '#EEF1F6' : '#FEF2F2', padding: '6px 10px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '4px', border: u.feedback ? '1px solid #DDE3EC' : '1px solid #FECACA', fontFamily: 'Source Sans 3, sans-serif' }}
                    data-testid={`feedback-btn-${u.id}`}>
                    <FileText size={10} /> {u.feedback ? 'Reviewed' : 'Feedback'}
                  </button>
                )}
                <button onClick={() => setEditingMap(p => ({ ...p, [u.id]: !p[u.id] }))}
                  style={{ fontSize: '11px', fontWeight: 600, color: '#5B6572', backgroundColor: '#EEF1F6', padding: '6px 10px', borderRadius: '3px', border: '1px solid #DDE3EC', fontFamily: 'Source Sans 3, sans-serif' }}
                  data-testid={`remap-btn-${u.id}`}>Re-map</button>
              </>
            )}
            {u.status !== 'transcribed' && (
              <button onClick={() => onDelete(u.id)} className="hover:opacity-70 transition-opacity p-1" style={{ color: '#DDE3EC' }}
                data-testid={`delete-btn-${u.id}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfAudit;
